interface GeocodingResult {
  lat: number
  lng: number
  address: string
  city: string
  country: string
}

interface ReverseGeocodingResult {
  address: string
  city: string
  country: string
  postalCode: string
}

class GeocodingService {
  private apiKey: string
  private baseUrl = 'https://geocode.search.hereapi.com/v1'

  constructor() {
    this.apiKey = import.meta.env.VITE_HERE_API_KEY
    if (!this.apiKey) {
      throw new Error('HERE Maps API key not configured')
    }
  }

  /**
   * Geocode city and optional neighborhood to get coordinates
   */
  async geocodeCityNeighborhood(city: string, neighborhood?: string): Promise<GeocodingResult | null> {
    try {
      const query = neighborhood ? `${neighborhood}, ${city}` : city
      const results = await this.geocode(query, 1)
      return results.length > 0 ? results[0] : null
    } catch (error) {
      console.error('City/neighborhood geocoding error:', error)
      return null
    }
  }

  /**
   * Geocode an address to get coordinates
   */
  async geocode(query: string, limit = 5): Promise<GeocodingResult[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/geocode?q=${encodeURIComponent(query)}&limit=${limit}&apiKey=${this.apiKey}`
      )

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.statusText}`)
      }

      const data = await response.json()
      
      return data.items?.map((item: any) => ({
        lat: item.position.lat,
        lng: item.position.lng,
        address: item.title || item.address?.label || '',
        city: item.address?.city || '',
        country: item.address?.country || ''
      })) || []
    } catch (error) {
      console.error('Geocoding error:', error)
      throw error
    }
  }

  /**
   * Reverse geocode coordinates to get address
   */
  async reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodingResult | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/revgeocode?at=${lat},${lng}&apiKey=${this.apiKey}`
      )

      if (!response.ok) {
        throw new Error(`Reverse geocoding failed: ${response.statusText}`)
      }

      const data = await response.json()
      const item = data.items?.[0]

      if (!item) return null

      return {
        address: item.title || item.address?.label || '',
        city: item.address?.city || '',
        country: item.address?.country || '',
        postalCode: item.address?.postalCode || ''
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error)
      return null
    }
  }

  /**
   * Search for places/POIs
   */
  async searchPlaces(query: string, at?: { lat: number; lng: number }, limit = 10) {
    try {
      let url = `${this.baseUrl}/discover?q=${encodeURIComponent(query)}&limit=${limit}&apiKey=${this.apiKey}`
      
      if (at) {
        url += `&at=${at.lat},${at.lng}`
      }

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Place search failed: ${response.statusText}`)
      }

      const data = await response.json()
      
      return data.items?.map((item: any) => ({
        id: item.id,
        lat: item.position.lat,
        lng: item.position.lng,
        title: item.title,
        address: item.address?.label || '',
        city: item.address?.city || '',
        country: item.address?.country || '',
        categories: item.categories?.map((cat: any) => cat.name) || [],
        distance: item.distance
      })) || []
    } catch (error) {
      console.error('Place search error:', error)
      throw error
    }
  }

  /**
   * Calculate distance between two points in kilometers
   */
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371 // Earth's radius in kilometers
    const dLat = this.toRad(lat2 - lat1)
    const dLng = this.toRad(lng2 - lng1)
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  /**
   * Get user's current location
   */
  async getCurrentLocation(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null)
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
        },
        (error) => {
          console.warn('Could not get current location:', error)
          resolve(null)
        },
        { timeout: 10000, enableHighAccuracy: true }
      )
    })
  }

  private toRad(value: number): number {
    return value * Math.PI / 180
  }
}

export const geocodingService = new GeocodingService()
export type { GeocodingResult, ReverseGeocodingResult }